import { useLazyQuery, useQuery } from '@apollo/client/react'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_SCORM_TRACKINGS } from '~/gql/scorm/scorm'
import { GQL_SESSIONS } from '~/gql/sessions/sessions'
import { client } from '~/helpers/apollo'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import { useListSearch } from '~/hooks/use-list-search'
import { EXPORT_MAX_ROWS, fetchAllForExport } from '~/pages/backoffice/exportPagination'
import SortableTableHead from '~/pages/backoffice/SortableTableHead'
import TablePagination from '~/pages/backoffice/TablePagination'
import { FilterSelect, SearchInput, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'
import { useTableQuery } from '~/pages/backoffice/useTableQuery'
import { downloadCSV } from '~/utils/exportCsv/utils'
import ScoreCell from './corrections/ScoreCell'
import ScoreCorrectionPanel from './corrections/ScoreCorrectionPanel'
import { completionLabelKey, successLabelKey } from './status-labels'

// Plafond d'heures plausible (1 an) : au-delà, la durée est aberrante (donnée
// legacy corrompue) et on n'affiche rien plutôt qu'un "666667h".
const MAX_PLAUSIBLE_HOURS = 366 * 24

function formatDuration(iso: string | null | undefined): string {
  if (!iso) return '-'
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
  if (!match) return iso
  if (match[1] && Number.parseInt(match[1], 10) > MAX_PLAUSIBLE_HOURS) return '-'
  const h = match[1] ? `${match[1]}h ` : ''
  const m = match[2] ? `${match[2]}m ` : ''
  const s = match[3] ? `${match[3]}s` : ''
  return `${h}${m}${s}`.trim() || '-'
}

// Affiche "raw / max" arrondi, sans notation scientifique. Renvoie null si une
// valeur est absente ou aberrante (donnée legacy corrompue, ex. 3.99e+49).
// Export CSV générique (produit white-label) : en-têtes construits via i18n.

function formatDateFr(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function StatisticsTable() {
  const [searchParams] = useSearchParams()
  const { page, pageSize, setPage, setPageSize, resetPage, handleSort, sortedInfo, queryArgs } = useTableQuery()
  // Amorçable par l'URL (`?sessionId=…`) : l'accueil (#201) y envoie depuis
  // chacun de ses faits, qui portent tous sur UN parcours précis. Sans ça, le
  // lien ouvrirait la promo entière et la question posée trois lignes plus haut
  // resterait à reformuler à la main.
  //
  // Lu une seule fois, à l'ouverture : le sélecteur reprend la main ensuite, et
  // ne réécrit pas l'URL. Une session inconnue (parcours supprimé, lien collé
  // depuis ailleurs) laisse simplement le sélecteur sur « toutes », plutôt que
  // d'afficher un filtre actif dont le nom n'apparaît nulle part.
  const [requestedSession, setSessionFilter] = useState(() => searchParams.get('sessionId') ?? '')
  const [groupFilter, setGroupFilter] = useState('')
  const { t } = useTranslation()

  // L'export CSV suit le droit d'exporter, comme le rapport PDF (#11), bien
  // qu'il se construise côté navigateur. Les filtres, eux, n'ont rien à
  // vérifier : voir le suivi suppose de voir parcours et apprenants.
  const canExport = useCan(TrainerCapability.STATISTICS_EXPORT)
  // Corriger un score est une décision d'admin (#161).
  const canCorrect = useAuth(state => state.payload?.role) === RoleEnum.Admin
  const [correctingId, setCorrectingId] = useState<string | null>(null)
  const { data: sessionsData } = useQuery(GQL_SESSIONS, { client })
  const { data: groupsData } = useQuery(GQL_GROUPS, { client })
  const sessions = sessionsData?.sessions ?? []
  const groups = groupsData?.groups ?? []

  // Le parcours amorcé par l'URL n'existe peut-être plus. Le garder filtrerait
  // sur un id absent du sélecteur : des chiffres restreints, et aucune mention à
  // l'écran de ce qui les restreint. On retombe donc sur « tous les parcours ».
  //
  // Dérivé plutôt que corrigé dans un effet : tant que la liste n'est pas
  // arrivée, le filtre reste appliqué — le remettre à zéro « en attendant »
  // ferait clignoter les chiffres de toute l'organisation sur un écran qu'on a
  // ouvert pour une promo précise.
  const sessionFilter = sessions.length === 0 || sessions.some(s => s.id === requestedSession)
    ? requestedSession
    : ''

  // Les deux filtres se cumulent : « le client A, sur la session de mars ».
  const filterBy = sessionFilter || groupFilter
    ? {
        ...(sessionFilter ? { sessionId: sessionFilter } : {}),
        ...(groupFilter ? { groupId: groupFilter } : {}),
      }
    : undefined

  // Débounce la recherche : la requête ne part qu'après une pause de saisie,
  // pour éviter une requête par frappe (et le ThrottlerException associé).
  const { search, setSearch, query: debouncedSearch } = useListSearch(resetPage)

  // cache-first : revenir sur une page déjà chargée est servi depuis le cache
  // (zéro requête réseau), ce qui évite de saturer le throttler en navigation rapide.
  const { data: currentData, previousData, refetch, loading } = useQuery(GQL_SCORM_TRACKINGS, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        ...queryArgs,
        searchInput: debouncedSearch,
        filterBy,
      },
    },
  })

  // Conserve l'affichage précédent tant que la nouvelle page charge ou en cas
  // d'erreur ponctuelle (ex. throttle) : le tableau ne se vide plus brutalement.
  const data = currentData ?? previousData

  // network-only : l'export part en plusieurs tranches, qui doivent toutes venir
  // de la même lecture serveur — une tranche servie depuis le cache mélangerait
  // deux états de la base dans un même fichier.
  const [fetchExportedTrackings] = useLazyQuery(GQL_SCORM_TRACKINGS, { fetchPolicy: 'network-only' })

  async function exportCsv() {
    const exported = await fetchAllForExport(async (skip, take) => {
      const chunk = await fetchExportedTrackings({
        variables: {
          input: {
            skip,
            take,
            searchInput: debouncedSearch,
            filterBy,
          },
        },
      })
      const result = chunk.data?.getScormTrackings
      return result?.trackings ? { rows: result.trackings, total: result.total } : null
    })
    if (!exported) return

    if (exported.truncated) {
      toast.warning(t('export.truncated', { max: EXPORT_MAX_ROWS, total: exported.total }))
    }

    downloadCSV(
      t('common.statistics'),
      [
        t('stats.export.email'),
        t('common.firstname'),
        t('common.lastname'),
        t('common.session'),
        t('stats.export.completion'),
        t('stats.export.success'),
        t('common.score'),
        t('scoreCorrection.csvColumn'),
        t('common.totalTime'),
        t('stats.export.completedAt'),
      ],
      exported.rows.map((tr) => {
        const score = tr.scoreRaw != null && tr.scoreMax != null
          ? `${tr.scoreRaw}/${tr.scoreMax}`
          : ''
        const totalTime = tr.totalTime ? formatDuration(tr.totalTime) : ''
        const validation = formatDateFr(tr.completedAt as unknown as string | null | undefined)
        return [
          /* Email */ tr.userEmail ?? '',
          /* Prénom */ tr.userFirstName ?? '',
          /* Nom */ tr.userLastName ?? '',
          /* Session */ tr.sessionName ?? '',
          /* Complétion */ t(completionLabelKey(tr.completionStatus)),
          /* Réussite */ t(successLabelKey(tr.successStatus)),
          /* Score */ score,
          /* Corrigé (#161) : un score corrigé ne se confond pas avec un calculé. */ tr.correctedScore != null ? t('scoreCorrection.csvYes') : '',
          /* Temps total */ totalTime,
          /* Complété le */ validation,
        ]
      }),
    )
  }

  const getCompletionBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="success">{t('status.completed')}</Badge>
      case 'INCOMPLETE':
        return <Badge variant="warning">{t('status.incomplete')}</Badge>
      case 'NOT_ATTEMPTED':
        return <Badge variant="secondary">{t('status.not_attempted')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  const getSuccessBadge = (status: string) => {
    switch (status) {
      case 'PASSED':
        return <Badge variant="success">{t('status.passed')}</Badge>
      case 'FAILED':
        return <Badge variant="destructive">{t('status.failed')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  return (
    <div className="space-y-4">
      {/*
        Les filtres ne commandent plus que cette table : la synthèse du haut est
        globale et vit maintenant dans le tableau de bord. Ils restent donc
        collés à elle, juste au-dessus des lignes qu'ils restreignent.
      */}
      <TableToolbar>
        <SearchInput value={search} onChange={e => setSearch(e.target.value)} />
        {/* Le filtre de parcours porte des noms saisis par l'admin, sans
            longueur bornée : c'est lui qui faisait déborder la barre. */}
        <FilterSelect
          value={sessionFilter}
          onChange={(e) => {
            setSessionFilter(e.target.value)
            resetPage()
          }}
          aria-label={t('common.sessions')}
        >
          <option value="">{t('common.allSessions')}</option>
          {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </FilterSelect>
        <FilterSelect
          value={groupFilter}
          onChange={(e) => {
            setGroupFilter(e.target.value)
            resetPage()
          }}
          aria-label={t('common.groups')}
        >
          <option value="">{t('groups.all')}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </FilterSelect>
        <Button
          variant="outline"
          aria-label={t('common.refresh')}
          onClick={() => void refetch()}
        >
          <RefreshCw className="size-4" />
        </Button>
        {canExport && (
          <ToolbarActions>
            <Button variant="outline" onClick={exportCsv}>
              {t('common.export')}
            </Button>
          </ToolbarActions>
        )}
      </TableToolbar>

      <div className="rounded-md border">
        <Table aria-label={t('common.routing.statistics')} {...tourAnchor('dashboard.table')}>
          <TableHeader>
            <TableRow>
              <SortableTableHead field="userEmail" label={t('common.email')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <SortableTableHead field="userFirstName" label={t('common.firstname')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <SortableTableHead field="userLastName" label={t('common.lastname')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <TableHead>{t('common.session')}</TableHead>
              <SortableTableHead field="completionStatus" label={t('common.completionStatus')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <SortableTableHead field="successStatus" label={t('common.successStatus')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <SortableTableHead field="scoreRaw" label={t('common.score')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <SortableTableHead field="completedAt" label={t('common.completedAt')} sortedInfo={sortedInfo} onSort={handleSort} className="p-0" />
              <TableHead>
                {t('common.totalTime')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center">
                      {t('common.loading')}
                      ...
                    </TableCell>
                  </TableRow>
                )
              : !data?.getScormTrackings.trackings?.length
                  ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center">
                          {t('common.empty')}
                        </TableCell>
                      </TableRow>
                    )
                  : (
                      data.getScormTrackings.trackings.map(tr => (
                        <TableRow key={tr.id}>
                          <TableCell>{tr.userEmail}</TableCell>
                          <TableCell>{tr.userFirstName}</TableCell>
                          <TableCell>{tr.userLastName}</TableCell>
                          <TableCell>{tr.sessionName}</TableCell>
                          <TableCell>{getCompletionBadge(tr.completionStatus)}</TableCell>
                          <TableCell>{getSuccessBadge(tr.successStatus)}</TableCell>
                          <TableCell>
                            <ScoreCell row={tr} onCorrect={canCorrect ? () => setCorrectingId(tr.id) : undefined} />
                          </TableCell>
                          <TableCell>
                            {tr.completedAt
                              ? new Date(tr.completedAt as unknown as string).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })
                              : '-'}
                          </TableCell>
                          <TableCell>{formatDuration(tr.totalTime)}</TableCell>
                        </TableRow>
                      ))
                    )}
          </TableBody>
        </Table>
      </div>

      {/*
        La ligne est relue dans les données courantes, pas gardée au clic : après
        une correction ou une annulation, le panneau montre le score à jour.
      */}
      <ScoreCorrectionPanel
        row={data?.getScormTrackings.trackings.find(tr => tr.id === correctingId) ?? null}
        onClose={() => setCorrectingId(null)}
        onChanged={refetch}
      />

      <TablePagination
        page={page}
        pageSize={pageSize}
        total={data?.getScormTrackings.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}

export default StatisticsTable
